import React, { useState, useEffect } from 'react';
import './SettingsPanel.css';

const SettingsPanel = ({ isOpen, onClose }) => {
    // Load initial settings from localStorage or use defaults
    const getInitialSettings = () => {
        const defaultSettings = {
            theme: 'light',
            fontSize: 'medium',
            autoScroll: true,
            compactMode: false,
            showTimestamps: false
        };

        const savedSettings = localStorage.getItem('chatSettings');
        if (savedSettings) {
            try {
                return { ...defaultSettings, ...JSON.parse(savedSettings) };
            } catch (error) {
                return defaultSettings;
            }
        }
        return defaultSettings;
    };

    const [settings, setSettings] = useState(getInitialSettings);

    useEffect(() => {
        // Only reload settings when panel is first opened, not every time
        if (isOpen) {
            const savedSettings = localStorage.getItem('chatSettings');
            if (savedSettings) {
                try {
                    const parsed = JSON.parse(savedSettings);
                    // Only update if there are actual differences to prevent unnecessary re-renders
                    const hasChanges = Object.keys(parsed).some(key => parsed[key] !== settings[key]);
                    if (hasChanges) {
                        setSettings(prevSettings => ({ ...prevSettings, ...parsed }));
                    }
                } catch (error) {
                    // Don't reset settings on error, keep current state
                }
            }
        }
    }, [isOpen]); // Removed settings dependency to prevent infinite loops

    useEffect(() => {
        // Save settings to localStorage with error handling
        try {
            localStorage.setItem('chatSettings', JSON.stringify(settings));
            
            // Apply theme to document
            document.documentElement.setAttribute('data-theme', settings.theme || 'light');
            document.documentElement.setAttribute('data-font-size', settings.fontSize || 'medium');
            document.documentElement.setAttribute('data-compact', settings.compactMode || false);
            
            // Dispatch custom event to notify other components of settings change
            window.dispatchEvent(new CustomEvent('settingsUpdated', { detail: settings }));
        } catch (error) {
            // If localStorage fails, still apply the visual changes
            document.documentElement.setAttribute('data-theme', settings.theme || 'light');
            document.documentElement.setAttribute('data-font-size', settings.fontSize || 'medium');
            document.documentElement.setAttribute('data-compact', settings.compactMode || false);
        }
    }, [settings]);

    const updateSetting = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const resetSettings = () => {
        const defaultSettings = {
            theme: 'light',
            fontSize: 'medium',
            autoScroll: true,
            compactMode: false,
            showTimestamps: false
        };
        setSettings(defaultSettings);
    };

    const exportSettings = () => {
        const dataStr = JSON.stringify(settings, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'chat-settings.json';
        link.click();
        URL.revokeObjectURL(url);
    };

    const importSettings = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const importedSettings = JSON.parse(e.target.result);
                    setSettings(importedSettings);
                } catch (error) {
                    alert('Invalid settings file');
                }
            };
            reader.readAsText(file);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="settings-overlay">
            <div className="settings-panel">
                <div className="settings-header">
                    <h3>Settings</h3>
                    <button onClick={onClose} className="close-button">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="settings-content">
                    <div className="setting-group">
                        <h4>Appearance</h4>
                        
                        <div className="setting-item">
                            <label>Theme</label>
                            <select 
                                value={settings.theme} 
                                onChange={(e) => updateSetting('theme', e.target.value)}
                            >
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                                <option value="auto">Auto</option>
                            </select>
                        </div>

                        <div className="setting-item">
                            <label>Font Size</label>
                            <select 
                                value={settings.fontSize} 
                                onChange={(e) => updateSetting('fontSize', e.target.value)}
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                                <option value="extra-large">Extra Large</option>
                            </select>
                        </div>

                        <div className="setting-item">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={settings.compactMode}
                                    onChange={(e) => updateSetting('compactMode', e.target.checked)}
                                />
                                Compact Mode
                            </label>
                        </div>

                        <div className="setting-item">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={settings.showTimestamps}
                                    onChange={(e) => updateSetting('showTimestamps', e.target.checked)}
                                />
                                Show Timestamps
                            </label>
                        </div>
                    </div>

                    <div className="setting-group">
                        <h4>Behavior</h4>
                        
                        <div className="setting-item">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={settings.autoScroll}
                                    onChange={(e) => updateSetting('autoScroll', e.target.checked)}
                                />
                                Auto-scroll to new messages
                            </label>
                        </div>
                    </div>

                    <div className="setting-group">
                        <h4>Data Management</h4>
                        
                        <div className="setting-actions">
                            <button onClick={exportSettings} className="action-button">
                                Export Settings
                            </button>
                            
                            <label className="action-button file-input-label">
                                Import Settings
                                <input
                                    type="file"
                                    accept=".json"
                                    onChange={importSettings}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            
                            <button onClick={resetSettings} className="action-button danger">
                                Reset to Defaults
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPanel;